/* eslint-env browser */
import { authenticationService } from './authenticationService';
import { apiService } from './apiService';

// Configuration
const POLL_INTERVAL = 60000; // 60 seconds
const ERROR_BACKOFF_MULTIPLIER = 2;
const MAX_BACKOFF = 300000; // 5 minutes

// State
let pollTimeoutId = null;
let lastHash = null;
let consecutiveErrors = 0;
let isPolling = false;
let authSubscription = null;

/**
 * Compute hash from roles and permissions for change detection
 * @param {Array} roles - Array of role names
 * @param {Array} permissions - Array of permission names
 * @returns {string} Hash string for comparison
 */
function computeHash(roles, permissions) {
    const rolesStr = Array.isArray(roles)
        ? [...roles].sort().join('|')
        : '';
    const permsStr = Array.isArray(permissions)
        ? [...permissions].sort().join('|')
        : '';
    return `${rolesStr}::${permsStr}`;
}

/**
 * Schedule the next poll with optional exponential backoff
 * @param {boolean} isError - Whether the last poll resulted in an error
 */
function scheduleNextPoll(isError = false) {
    if (pollTimeoutId) {
        window.clearTimeout(pollTimeoutId);
    }

    let delay = POLL_INTERVAL;

    if (isError && consecutiveErrors > 0) {
        // Exponential backoff: 120s, 240s, 300s (capped)
        delay = Math.min(
            POLL_INTERVAL * Math.pow(ERROR_BACKOFF_MULTIPLIER, consecutiveErrors),
            MAX_BACKOFF
        );
    }

    pollTimeoutId = setTimeout(() => {
        pollLoginStatus();
    }, delay);
}

/**
 * Poll the login-status endpoint and update user if changes detected
 */
async function pollLoginStatus() {
    // Safety check: ensure user is still logged in
    if (!authenticationService.currentUserValue) {
        stopPolling();
        return;
    }

    try {
        const response = await apiService.apiGet(
            `${import.meta.env.VITE_API_URL}/login-status`
        );

        // Extract roles and permissions from response
        const { roles, permissions } = response;

        const newHash = computeHash(roles, permissions);

        if (lastHash === null) {
            // First poll, initialize hash
            lastHash = newHash;
        } else if (lastHash !== newHash) {
            // Changes detected, update user
           
            authenticationService.updateRolesPermissions(roles, permissions);
            lastHash = newHash;
        }

        // Reset error counter on success
        consecutiveErrors = 0;
        scheduleNextPoll(false);

    } catch (error) {
        consecutiveErrors++;
        console.warn(
            `Login status poll failed (attempt ${consecutiveErrors}):`,
            error
        );
        scheduleNextPoll(true);
    }
}

/**
 * Start the polling cycle
 */
function startPolling() {
    if (isPolling) return;

    isPolling = true;
    lastHash = null;
    consecutiveErrors = 0;

    // Start first poll immediately
    pollLoginStatus();
}

/**
 * Stop the polling cycle and cleanup
 */
function stopPolling() {
    if (pollTimeoutId) {
        window.clearTimeout(pollTimeoutId);
        pollTimeoutId = null;
    }
    isPolling = false;
    lastHash = null;
    consecutiveErrors = 0;
}

/**
 * Initialize the polling service
 * Subscribes to authentication state and starts/stops polling automatically
 */
function init() {
    // Subscribe to authentication state changes
    authSubscription = authenticationService.currentUser.subscribe(user => {
        if (user) {
            // User logged in, start polling if not already polling
            if (!isPolling) {
                startPolling();
            }
        } else {
            // User logged out, stop polling
            stopPolling();
        }
    });
}

/**
 * Cleanup the polling service
 * Stops polling and unsubscribes from authentication state
 */
function destroy() {
    stopPolling();
    if (authSubscription) {
        authSubscription.unsubscribe();
        authSubscription = null;
    }
}

export const loginStatusPollingService = {
    init,
    destroy
};
